import { BodyZone, BodyZoneId } from '../types';

/**
 * Get human-readable name for a body zone
 */
export const getBodyZoneName = (id: BodyZoneId): string => {
  // Special cases where name is customized
  switch (id) {
    case BodyZoneId.face:
      return 'Face';
    case BodyZoneId.posteriorHead:
      return 'Back of Head';
    case BodyZoneId.pubicRegion:
      return 'Pubic Area';
    case BodyZoneId.leftButtocks:
      return 'Left Buttock';
    case BodyZoneId.rightButtocks:
      return 'Right Buttock';
    case BodyZoneId.leftFoot:
      return 'Left Foot';
    case BodyZoneId.rightFoot:
      return 'Right Foot';
    default:
      // Standard formatting for other zones
      // Extract body part and side from the enum name
      const enumName = id;
      
      // Determine if it's anterior or posterior
      const isAnterior = enumName.startsWith('anterior');
      const isPosterior = enumName.startsWith('posterior');
      
      // Extract the body part name
      let bodyPart: string;
      if (isAnterior) {
        bodyPart = enumName.substring(8); // Remove 'anterior'
      } else if (isPosterior) {
        bodyPart = enumName.substring(9); // Remove 'posterior'
      } else {
        bodyPart = enumName;
      }
      
      // Format the body part name (camelCase to Title Case)
      const formattedBodyPart = bodyPart.replace(/([A-Z])/g, ' $1').trim();
      const capitalizedBodyPart = formattedBodyPart.charAt(0).toUpperCase() + formattedBodyPart.slice(1);
      
      // Add side information if applicable
      if (isAnterior || isPosterior) {
        const sideName = isAnterior ? 'Front' : 'Back';
        return `${capitalizedBodyPart} (${sideName})`;
      } else {
        return capitalizedBodyPart;
      }
  }
};

// Static zone definitions for anterior (front) view
export const anteriorZones: BodyZone[] = [
  // Neck
  {
    id: BodyZoneId.anteriorNeck,
    svgAssetPath: '/assets/scan_locations/front/neck.svg',
    top: 0.150,
    left: 0.409,
    height: 0.041,
  },
  // Head
  {
    id: BodyZoneId.face,
    svgAssetPath: '/assets/scan_locations/front/head.svg',
    top: 0.0129,
    left: 0.382,
    height: 0.126,
  },
  // Trunk
  {
    id: BodyZoneId.anteriorTrunk,
    svgAssetPath: '/assets/scan_locations/front/trunk.svg',
    top: 0.185,
    left: 0.271,
    height: 0.285,
  },
  // Right Arm
  {
    id: BodyZoneId.anteriorRightArm,
    svgAssetPath: '/assets/scan_locations/front/right_arm.svg',
    top: 0.192,
    left: 0.102,
    height: 0.302,
  },
  // Left Arm
  {
    id: BodyZoneId.anteriorLeftArm,
    svgAssetPath: '/assets/scan_locations/front/left_arm.svg',
    top: 0.192,
    left: 0.697,
    height: 0.302,
  },
  // Right Hand
  {
    id: BodyZoneId.anteriorRightHand,
    svgAssetPath: '/assets/scan_locations/front/right_hand.svg',
    top: 0.497,
    left: 0.058,
    height: 0.042,
  },
  // Left Hand
  {
    id: BodyZoneId.anteriorLeftHand,
    svgAssetPath: '/assets/scan_locations/front/left_hand.svg',
    top: 0.497,
    left: 0.840,
    height: 0.042,
  },
  // Right Thigh
  {
    id: BodyZoneId.anteriorRightThigh,
    svgAssetPath: '/assets/scan_locations/front/right_thigh.svg',
    top: 0.502,
    left: 0.299,
    height: 0.225,
  },
  // Left Thigh
  {
    id: BodyZoneId.anteriorLeftThigh,
    svgAssetPath: '/assets/scan_locations/front/left_thigh.svg',
    top: 0.502,
    left: 0.514,
    height: 0.225,
  },
  // Pubic Region (after thighs so it renders on top)
  {
    id: BodyZoneId.pubicRegion,
    svgAssetPath: '/assets/scan_locations/front/pubic_region.svg',
    top: 0.460,
    left: 0.310,
    height: 0.085,
  },
  // Right Calf
  {
    id: BodyZoneId.anteriorRightCalf,
    svgAssetPath: '/assets/scan_locations/front/right_calf.svg',
    top: 0.734,
    left: 0.344,
    height: 0.188,
  },
  // Left Calf
  {
    id: BodyZoneId.anteriorLeftCalf,
    svgAssetPath: '/assets/scan_locations/front/left_calf.svg',
    top: 0.734,
    left: 0.512,
    height: 0.188,
  },
  // Right Foot
  {
    id: BodyZoneId.rightFoot,
    svgAssetPath: '/assets/scan_locations/front/right_foot.svg',
    top: 0.928,
    left: 0.361,
    height: 0.048,
  },
  // Left Foot
  {
    id: BodyZoneId.leftFoot,
    svgAssetPath: '/assets/scan_locations/front/left_foot.svg',
    top: 0.928,
    left: 0.515,
    height: 0.048,
  },
];

// Static zone definitions for posterior (back) view
export const posteriorZones: BodyZone[] = [
  // Head (back)
  {
    id: BodyZoneId.posteriorHead,
    svgAssetPath: '/assets/scan_locations/back/head.svg',
    top: 0.011,
    left: 0.378,
    height: 0.122,
  },
  // Trunk (upper back)
  {
    id: BodyZoneId.posteriorTrunk,
    svgAssetPath: '/assets/scan_locations/back/trunk.svg',
    top: 0.139,
    left: 0.257,
    height: 0.3341,
  },
  // Right Arm
  {
    id: BodyZoneId.posteriorRightArm,
    svgAssetPath: '/assets/scan_locations/back/right_arm.svg',
    top: 0.213,
    left: 0.735,
    height: 0.283,
  },
  // Left Arm
  {
    id: BodyZoneId.posteriorLeftArm,
    svgAssetPath: '/assets/scan_locations/back/left_arm.svg',
    top: 0.213,
    left: 0.104,
    height: 0.283,
  },
  // Right Hand
  {
    id: BodyZoneId.posteriorRightHand,
    svgAssetPath: '/assets/scan_locations/back/right_hand.svg',
    top: 0.497,
    left: 0.839,
    height: 0.042,
  },
  // Left Hand
  {
    id: BodyZoneId.posteriorLeftHand,
    svgAssetPath: '/assets/scan_locations/back/left_hand.svg',
    top: 0.497,
    left: 0.0615,
    height: 0.042,
  },
  // Right Buttocks
  {
    id: BodyZoneId.rightButtocks,
    svgAssetPath: '/assets/scan_locations/back/left_buttocks.svg',
    top: 0.457,
    left: 0.305,
    height: 0.096,
  },
  // Left Buttocks
  {
    id: BodyZoneId.leftButtocks,
    svgAssetPath: '/assets/scan_locations/back/right_buttocks.svg',
    top: 0.457,
    left: 0.502,
    height: 0.096,
  },
  // Right Thigh (posterior)
  {
    id: BodyZoneId.posteriorRightThigh,
    svgAssetPath: '/assets/scan_locations/back/right_thigh.svg',
    top: 0.556,
    left: 0.520,
    height: 0.172,
  },
  // Left Thigh (posterior)
  {
    id: BodyZoneId.posteriorLeftThigh,
    svgAssetPath: '/assets/scan_locations/back/left_thigh.svg',
    top: 0.556,
    left: 0.299,
    height: 0.172,
  },
  // Right Calf (posterior)
  {
    id: BodyZoneId.posteriorRightCalf,
    svgAssetPath: '/assets/scan_locations/back/right_calf.svg',
    top: 0.737,
    left: 0.515,
    height: 0.257,
  },
  // Left Calf (posterior)
  {
    id: BodyZoneId.posteriorLeftCalf,
    svgAssetPath: '/assets/scan_locations/back/left_calf.svg',
    top: 0.737,
    left: 0.347,
    height: 0.257,
  },
];
